/** Standalone desktop composer summoned beside the pointer or an anchor. */
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";
import { createPortal } from "react-dom";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { InputBarTargetComposer } from "@/components/panels/input-bar-target-composer";
import { Button } from "@/components/ui";
import { SUMMONED_INPUT_Z_INDEX, SummonedInputSurfaceScope } from "@/components/ui/summoned-input-surface-context";
import { getDesktopChatAuthScope } from "./desktop-chat-auth-scope";
import type { ComposerFieldHandle } from "@/components/panels/standalone-bar-field";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import type { DictationSurface } from "@/components/panels/input-bar-types";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import { useRevealStage } from "@/hooks/use-reveal-stage";
import { RETRACT_MS } from "@/lib/reveal-motion";
import { submitInputBarTarget } from "@/lib/input-bar-target-submit";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import { isTauri } from "@/lib/is-tauri";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useCanvasStore } from "@/stores/canvas-store";
import type { CursorInputSubmitMode, SummonSelection } from "@/stores/canvas-store";
import { useSelectionQuotePrefill } from "@/hooks/use-selection-quote-prefill";
import { useStandaloneComposerDraft } from "@/stores/standalone-composer-draft-store";
import { getWindowDisplayId } from "./use-window-role";
import { dismissCursorInputEverywhere } from "./cursor-input-dismissal";
import { useCursorInputShown } from "./cursor-input-presence";
import { commentDictationClaimedBy } from "./comment-dictation-target";
import { isDictationAnywhereBusy } from "./dictation-anywhere/dictation-anywhere-store";
import { useShiftTap } from "./use-shift-tap";
import { useSummonedInputFocus } from "./use-summoned-input-focus";
import { useStandaloneInputDrag } from "./use-standalone-input-drag";
import {
  acceptTextAttentionRetry,
  previewTextAttentionRetry,
  useTextAttentionCapture,
  type TextAttentionRetryPreview,
} from "./use-text-attention-capture";
import { appendTextAttentionPrompt, registerTextAttentionScreens, textAttentionSendState } from "./text-attention-prompt";
import {
  clearTextAttentionDraft,
  removeTextAttentionCapture,
  removeTextAttentionChapter,
  setTextAttentionScope,
  textChapterId,
  useTextAttentionStore,
} from "./text-attention-store";
import { startTextAttentionSession, stopTextAttentionSession, updateTextAttentionComment } from "./use-text-attention";
import { ScreenRecordingDraft } from "./screen-recording-draft";
import { useScreenRecordingDraft } from "./use-screen-recording-draft";
import { clearPreparedScreenRecordings, setScreenRecordingTextScope, useScreenRecordingStore } from "./screen-recording-store";
import { TextAttentionDraftComments } from "./text-attention-draft-comments";
import { useT } from "@/i18n";
import { compactCursorMessages } from "@/i18n/messages/compact-cursor";
import { commonMessages } from "@/i18n/messages/common";

export const STANDALONE_BAR_DICTATION_ID = "input-bar-target:standalone";

type VisibleSummon = {
  x: number;
  y: number;
  placement?: "cursor" | "anchor" | "bottom";
  summonId?: number;
  summonAt?: number;
  selection?: SummonSelection;
  agentKey?: string;
  submitMode?: CursorInputSubmitMode;
  submitEventName?: string;
};

export function CompactCursorInputOverlay({ ctx }: { ctx: CursorInputContextValue }) {
  const pending = useCanvasStore((state) => state.pendingCursorSummon);
  const myDisplayId = getWindowDisplayId();
  const visiblePending = pending && (pending.displayId == null || pending.displayId === myDisplayId)
    ? pending as VisibleSummon
    : null;
  const snapshotRef = useRef<VisibleSummon | null>(visiblePending);
  if (visiblePending) snapshotRef.current = visiblePending;
  const { draft, setValue: setDraft, updateDraft } = useStandaloneComposerDraft();
  const value = draft.text;
  const valueRef = useRef(value);
  valueRef.current = value;
  const setValue = useCallback((next: SetStateAction<string>) => {
    const resolved = typeof next === "function" ? next(valueRef.current) : next;
    valueRef.current = resolved;
    setDraft(resolved);
  }, [setDraft]);
  const fieldRef = useRef<ComposerFieldHandle | null>(null);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const recordingDraft = useScreenRecordingDraft(ctxRef);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const dictationTarget = useInputBarTarget((state) => state.target);
  const t = useT(compactCursorMessages);
  const tc = useT(commonMessages);
  const [submitting, setSubmitting] = useState(false);
  const [retryPreviews, setRetryPreviews] = useState<Record<string, TextAttentionRetryPreview>>({});
  const [retryBusyId, setRetryBusyId] = useState<string | null>(null);
  const retryGenerationRef = useRef(0);
  const visible = Boolean(visiblePending) && !submitting;
  useCursorInputShown(visible);
  const authScope = getDesktopChatAuthScope();
  const attentionScopeKey = JSON.stringify([authScope?.accountId, authScope?.epoch, ctx.projectId]);
  useEffect(() => { setTextAttentionScope(attentionScopeKey); setScreenRecordingTextScope(attentionScopeKey); }, [attentionScopeKey]);
  const attention = useTextAttentionStore();
  const attentionSendState = textAttentionSendState(attention, ctx.attachedImages);
  const images = ctx.attachedImages.map((image) => {
    const capture = Object.values(attention.captures).find((item) =>
      item.attachment?.id === image.id);
    if (!capture) return image;
    const annotationNumbers = attention.annotations.filter((item) =>
      textChapterId(item.id.split("/")[0]!, item.displayId, item.revision) === capture.chapterId,
    ).map((item) => item.number);
    return { ...image, annotationNumbers };
  });
  const reveal = useRevealStage(visible, { onExited: () => {
    snapshotRef.current = null;
  } });

  const dismiss = useCallback(() => dismissCursorInputEverywhere(), []);
  const hasDraft = useCallback(
    () => !!valueRef.current.trim() || !!ctxRef.current.attachedImages?.length ||
      useTextAttentionStore.getState().annotations.length > 0 || Object.values(useScreenRecordingStore.getState().clips).some((clip) => useScreenRecordingStore.getState().sessions[clip.attentionSessionId] === "text"),
    [],
  );
  const dismissIfEmpty = useCallback(() => {
    if (!hasDraft()) dismiss();
  }, [dismiss, hasDraft]);

  const focusKey = visiblePending?.summonId ?? visiblePending?.summonAt ?? "active";
  useEffect(() => {
    retryGenerationRef.current += 1;
    setRetryPreviews({});
  }, [attentionScopeKey, focusKey]);
  useTextAttentionCapture(visible, ctxRef);
  useEffect(() => {
    if (!visible || !isTauri()) return;
    let disposed = false;
    let recordingId: string | null = null;
    void startTextAttentionSession().then((id) => {
      recordingId = id;
      if (disposed) void stopTextAttentionSession(id).catch(() => {});
    }).catch(() => {});
    return () => {
      disposed = true;
      if (recordingId) void stopTextAttentionSession(recordingId).catch(() => {});
    };
  }, [attentionScopeKey, focusKey, visible]);
  useSummonedInputFocus(fieldRef, focusKey, visible && reveal.mounted && reveal.stage !== "exit");
  const dragSummon = visiblePending ?? snapshotRef.current;
  const drag = useStandaloneInputDrag(
    dragSummon ? dragSummon.summonId ?? dragSummon.summonAt ?? "active" : null,
    visible && reveal.stage === "idle",
  );

  // While shown, the voice shortcut dictates into this bar.
  const attachDictatedImages = useCallback((images: ImageAttachment[]) => {
    if (!isSubagentTarget(useInputBarTarget.getState().target)) ctxRef.current.onAttachImages?.(images);
  }, []);
  const subagentDictation = isSubagentTarget(dictationTarget);
  const dictationSurface = useMemo<DictationSurface | undefined>(
    () => visible ? { disableScreenshotCapture: subagentDictation, onImages: attachDictatedImages } : undefined,
    [attachDictatedImages, subagentDictation, visible],
  );

  useSelectionQuotePrefill(visiblePending?.selection, visiblePending?.summonId, setValue);
  // While dictation anywhere runs, Shift belongs to its drawn marks.
  useShiftTap(() => {
    if (!isDictationAnywhereBusy()) ctx.setScreenshotIntent?.(!ctx.screenshotIntent);
  }, {
    enabled: visible && Boolean(ctx.setScreenshotIntent),
  });

  useEffect(() => {
    if (!visible) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-input-bar-root],[data-denker-surface-role="menu"]')) return;
      dismissIfEmpty();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [dismissIfEmpty, visible]);

  useEffect(() => {
    if (!visible || !isTauri()) return;
    let disposed = false;
    let unlisten: (() => void) | null = null;
    let blurTimer: ReturnType<typeof setTimeout> | null = null;
    void getCurrentWindow().onFocusChanged(({ payload: focused }) => {
      if (!focused && !commentDictationClaimedBy(STANDALONE_BAR_DICTATION_ID)) {
        // The native Shift-drag consumes the pointer press. Its stroke event
        // can arrive just after focus leaves this webview on another display.
        blurTimer = setTimeout(dismissIfEmpty, 150);
      } else if (blurTimer) {
        clearTimeout(blurTimer);
        blurTimer = null;
      }
    }).then((stop) => {
      if (disposed) stop();
      else unlisten = stop;
    });
    return () => { disposed = true; unlisten?.(); if (blurTimer) clearTimeout(blurTimer); };
  }, [dismissIfEmpty, visible]);

  const summon = visiblePending ?? snapshotRef.current;
  // A failed send can finish on the same frame as retract completion. Keep
  // the composer mounted while its summon still exists so its chip and route
  // survive that frame and the next reveal. Once retracted it stays hidden,
  // or a slow send leaves its reveal dot parked at the summon point.
  if ((!reveal.mounted && !visiblePending) || !summon) return null;

  // Escape MasterLayout's fixed stacking context so a summon remains visible
  // even when its cursor anchor overlaps the onboarding guide.
  return createPortal(
    <SummonedInputSurfaceScope>
    <div
      ref={drag.containerRef}
      {...drag.dragHandlers}
      data-interactive="true"
      data-testid="compact-cursor-input"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: SUMMONED_INPUT_Z_INDEX,
        pointerEvents: submitting || reveal.stage === "exit" ? "none" : undefined,
        visibility: reveal.mounted ? undefined : "hidden",
      }}
    >
      <InputBarTargetComposer
        variant="standalone"
        revealStage={reveal.stage}
        inputHandleRef={fieldRef}
        dictationId={STANDALONE_BAR_DICTATION_ID}
        dictationSurface={dictationSurface}
        suppressPrediction={summon.submitMode === "onboarding_demo_suppress"}
        cursorAnchor={{ type: "screen", localX: summon.x, localY: summon.y }}
        cursorPlacement={summon.placement ?? "cursor"}
        compact
        defaultAgentName="Denker"
        disabled={submitting || reveal.stage === "exit"}
        hasExtraContent={recordingDraft.hasRecordings}
        prepareQuestionReply={async () => {
          try { const prepared = await recordingDraft.prepare(); setRecordingError(null); return { screenRecordings: prepared.context, images: prepared.images, onSent: () => clearPreparedScreenRecordings(prepared.context?.clips.map((clip) => clip.id) ?? []) }; }
          catch (error) { setRecordingError(String(error)); return false; }
        }}
        submitBlocked={attentionSendState === "pending"}
        annotationNotice={attentionSendState === "pending" ? (
          <div className="rounded-preview bg-glass-fill-heavy px-2 py-1 text-appkit-caption text-secondary" data-testid="text-attention-status">
            {Object.values(attention.captures).some((item) => item.status === "error")
              ? t("annotationNeedsScreenshot")
              : t("capturingAnnotations")}
            {Object.values(attention.captures).filter((item) => item.status === "error").map((item) => (
              <div key={item.chapterId} className="flex items-center gap-1">
                <span>{t("marks", { numbers: attention.annotations.filter((annotation) =>
                  textChapterId(annotation.id.split("/")[0]!, annotation.displayId, annotation.revision) === item.chapterId,
                ).map((annotation) => annotation.number).join(", ") })}</span>
                <Button type="button" variant="ghost" size="sm"
                  disabled={retryBusyId === item.chapterId}
                  data-testid={`text-attention-retry-${item.chapterId}`}
                  onClick={() => {
                    const generation = retryGenerationRef.current;
                    setRetryBusyId(item.chapterId);
                    void previewTextAttentionRetry(item.chapterId).then((candidate) => {
                      if (candidate && generation === retryGenerationRef.current &&
                        useTextAttentionStore.getState().captures[item.chapterId]?.status === "error") {
                        setRetryPreviews((current) => ({ ...current, [item.chapterId]: candidate }));
                      }
                    }).catch(() => {}).finally(() => setRetryBusyId(null));
                  }}>{t("reviewScreenshot")}</Button>
                <Button type="button" variant="ghost" size="sm"
                  data-testid={`text-attention-remove-failed-${item.chapterId}`}
                  onClick={() => {
                    retryGenerationRef.current += 1;
                    removeTextAttentionChapter(item.chapterId);
                    setRetryPreviews((current) => {
                      const next = { ...current };
                      delete next[item.chapterId];
                      return next;
                    });
                  }}>{t("removeMarks")}</Button>
                {retryPreviews[item.chapterId] ? (
                  <div data-testid={`text-attention-retry-preview-${item.chapterId}`}>
                    <span>{t("checkMarkedView")}</span>
                    <img
                      src={`data:image/jpeg;base64,${retryPreviews[item.chapterId]!.preview.jpegBase64}`}
                      alt={t("screenForMarks")}
                      className="max-h-40 max-w-60 rounded-preview object-contain"
                    />
                    <Button type="button" variant="ghost" size="sm"
                      data-testid={`text-attention-accept-retry-${item.chapterId}`}
                      onClick={() => {
                        const candidate = retryPreviews[item.chapterId];
                        if (!candidate) return;
                        void acceptTextAttentionRetry(item.chapterId, candidate, ctxRef).then(() => {
                          if (useTextAttentionStore.getState().captures[item.chapterId]?.status === "ready") {
                            setRetryPreviews((current) => {
                              const next = { ...current };
                              delete next[item.chapterId];
                              return next;
                            });
                          }
                        });
                      }}>{t("useThisScreenshot")}</Button>
                    <Button type="button" variant="ghost" size="sm"
                      data-testid={`text-attention-cancel-retry-${item.chapterId}`}
                      onClick={() => setRetryPreviews((current) => {
                        const next = { ...current };
                        delete next[item.chapterId];
                        return next;
                      })}>{tc("cancel")}</Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : undefined}
        onClose={dismissIfEmpty}
        value={value}
        onChange={setValue}
        onSubmit={async () => {
          const submittedValue = valueRef.current;
          const attentionSnapshot = useTextAttentionStore.getState();
          if (textAttentionSendState(attentionSnapshot, ctxRef.current.attachedImages) === "pending") return false;
          const prompt = appendTextAttentionPrompt(
            submittedValue, attentionSnapshot, ctxRef.current.attachedImages,
          );
          if (attentionSnapshot.annotations.length) {
            registerTextAttentionScreens(attentionSnapshot, ctxRef.current.attachedImages);
          }
          const submittedSummon = pending;
          const submittedSummonId = summon.summonId;
          if (summon.submitMode === "onboarding_demo_suppress") {
            if (summon.submitEventName) window.dispatchEvent(new Event(summon.submitEventName));
            updateDraft({ text: "", chip: null, previousRoute: null }, submittedValue);
            dismiss();
            return true;
          }
          setSubmitting(true);
          const started = Date.now();
          try {
            const recordings = await recordingDraft.prepare();
            setRecordingError(null);
            const sent = await submitInputBarTarget(
              ctx, prompt, summon.agentKey,
              { skipAutoCapture: attentionSnapshot.annotations.length > 0 || !!recordings.context, screenRecordings: recordings.context, recordingImages: recordings.images },
            );
            if (sent === false) {
              await new Promise((resolve) => setTimeout(resolve, Math.max(0, RETRACT_MS - (Date.now() - started))));
              setSubmitting(false);
              return false;
            }
            const current = useCanvasStore.getState().pendingCursorSummon;
            const stillSame = submittedSummonId !== undefined
              ? current?.summonId === submittedSummonId
              : current === submittedSummon;
            clearPreparedScreenRecordings(recordings.context?.clips.map((clip) => clip.id) ?? []);
            if (stillSame && valueRef.current === submittedValue) {
              updateDraft({ text: "", chip: null, previousRoute: null }, submittedValue);
              clearTextAttentionDraft();
              const recordingState = useScreenRecordingStore.getState();
              if (!Object.values(recordingState.clips).some((clip) => recordingState.sessions[clip.attentionSessionId] === "text")) dismiss();
            }
            setSubmitting(false);
            return true;
          } catch (error) {
            setRecordingError(String(error));
            await new Promise((resolve) => setTimeout(resolve, Math.max(0, RETRACT_MS - (Date.now() - started))));
            setSubmitting(false);
            throw error;
          }
        }}
        isStreaming={ctx.isStreaming}
        onStop={ctx.onStop}
        agents={ctx.inputAgents}
        selectedAgentIds={ctx.selectedAgentIds}
        onSelectAgents={ctx.onSelectAgents}
        projectId={ctx.projectId}
        spaceId={ctx.spaceId}
        onOpenSettings={ctx.onOpenSettings}
        onOpenSettingsForAgent={ctx.onOpenSettingsForAgent}
        onRemoveAgent={ctx.onRemoveAgent}
        images={images}
        annotationSummary={<><ScreenRecordingDraft />{recordingError && <div role="alert" className="px-2 text-appkit-caption text-secondary" data-testid="screen-recording-send-error">{recordingError}</div>}<TextAttentionDraftComments
          annotations={attention.annotations}
          onCommentChange={updateTextAttentionComment}
        /></>}
        onRemoveImage={(id) => {
          removeTextAttentionCapture(id);
          ctx.onRemoveImage(id);
        }}
        onImageFile={ctx.onImageFile}
        slashCommands={ctx.slashCommands}
        targetPicker
        maxParallelAgents={ctx.maxParallelAgents}
        onChangeMaxParallelAgents={ctx.onChangeMaxParallelAgents}
        onAgentInspect={ctx.onAgentInspect}
        focusedAgent={ctx.focusedAgentInfo}
        providers={ctx.providers}
        onUpdateAgentModel={ctx.onUpdateAgentModel}
        onUpdateAgentEffort={ctx.onUpdateAgentEffort}
        companionDisconnected={ctx.companionDisconnected}
        onFixCompanion={ctx.onFixCompanion}
      />
    </div>
    </SummonedInputSurfaceScope>,
    document.body,
  );
}
